import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { Ellipsis, Handshake, Home, ListTodo, Plus, Users } from "lucide-react";
import { useTranslate } from "ra-core";
import { Link, matchPath, useLocation, useMatch } from "react-router";
import { ContactCreateSheet } from "../contacts/ContactCreateSheet";
import { useState } from "react";
import { NoteCreateSheet } from "../notes/NoteCreateSheet";
import { TaskCreateSheet } from "../tasks/TaskCreateSheet";
// TENAX: "More" panel and organizations on the phone (task 1.12, D-044)
import { MORE_PATHS, MoreSheet } from "@/tenax/mobile/MoreSheet";
import { CompanyCreateSheet } from "@/tenax/mobile/CompanySheets";

export const MobileNavigation = () => {
  const location = useLocation();
  const translate = useTranslate();

  let currentPath: string | boolean = "/";
  if (matchPath("/", location.pathname)) {
    currentPath = "/";
  } else if (matchPath("/contacts/*", location.pathname)) {
    currentPath = "/contacts";
  } else if (matchPath("/companies/*", location.pathname)) {
    currentPath = "/companies";
  } else if (matchPath("/tasks/*", location.pathname)) {
    currentPath = "/tasks";
  } else if (matchPath("/deals/*", location.pathname)) {
    currentPath = "/deals";
  } else if (
    // TENAX: the sections in the "More" panel light up its button
    MORE_PATHS.some((path) => matchPath(`${path}/*`, location.pathname))
  ) {
    currentPath = "more";
  } else {
    currentPath = false;
  }

  return (
    <nav
      aria-label={translate("crm.navigation.label")}
      // TENAX: the bottom inset comes from env(safe-area-inset-bottom)
      // (viewport-fit=cover in index.html) instead of a fixed 15 px on iOS
      // only, and the six buttons share the whole width (task 1.12)
      className="fixed bottom-0 left-0 right-0 z-50 bg-secondary pb-[env(safe-area-inset-bottom)]"
    >
      <div className="flex h-14 items-center justify-around px-1">
        <>
          <NavigationButton
            href="/"
            Icon={Home}
            label={translate("tenax.today.title")} // TENAX: Today screen (task 1.4)
            isActive={currentPath === "/"}
          />
          <NavigationButton
            href="/contacts"
            Icon={Users}
            label={translate("tenax.mobile.people")} // TENAX: "Persone" (task 1.12)
            isActive={currentPath === "/contacts"}
          />
          {/* TENAX: deals on the phone (task 1.7) */}
          <NavigationButton
            href="/deals"
            Icon={Handshake}
            label={translate("resources.deals.name", { smart_count: 2 })}
            isActive={currentPath === "/deals"}
          />
          <CreateButton />
          <NavigationButton
            href="/tasks"
            Icon={ListTodo}
            label={translate("resources.tasks.name", { smart_count: 2 })}
            isActive={currentPath === "/tasks"}
          />
          {/* TENAX: organizations, agreements, test rackets and settings (task 1.12) */}
          <MoreButton isActive={currentPath === "more"} />
        </>
      </div>
    </nav>
  );
};

const NavigationButton = ({
  href,
  Icon,
  label,
  isActive,
}: {
  href: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  isActive: boolean;
}) => (
  <Button
    asChild
    variant="ghost"
    className={cn(
      // TENAX: the six buttons share the width, at least 48 px each, with
      // larger labels (task 1.12; w-14 since task 1.7)
      "flex-col gap-1 h-auto min-h-12 min-w-12 flex-1 py-1.5 px-0.5 rounded-md",
      isActive ? null : "text-muted-foreground",
    )}
  >
    <Link to={href}>
      <Icon className="size-6" />
      <span className="text-[0.7rem] font-medium leading-none">{label}</span>
    </Link>
  </Button>
);

const CreateButton = () => {
  const translate = useTranslate();
  const contact_id = useMatch("/contacts/:id/*")?.params.id;
  const [contactCreateOpen, setContactCreateOpen] = useState(false);
  const [noteCreateOpen, setNoteCreateOpen] = useState(false);
  const [taskCreateOpen, setTaskCreateOpen] = useState(false);
  // TENAX: new organization from the phone (task 1.12)
  const [companyCreateOpen, setCompanyCreateOpen] = useState(false);

  return (
    <>
      <CompanyCreateSheet
        open={companyCreateOpen}
        onOpenChange={setCompanyCreateOpen}
      />
      <ContactCreateSheet
        open={contactCreateOpen}
        onOpenChange={setContactCreateOpen}
      />
      <NoteCreateSheet
        open={noteCreateOpen}
        onOpenChange={setNoteCreateOpen}
        contact_id={contact_id}
      />
      <TaskCreateSheet
        open={taskCreateOpen}
        onOpenChange={setTaskCreateOpen}
        contact_id={contact_id}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="default"
            size="icon"
            className="h-16 w-16 rounded-full -mt-3"
            aria-label={translate("ra.action.create")}
          >
            <Plus className="size-10" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => {
              setContactCreateOpen(true);
            }}
          >
            {translate("resources.contacts.forcedCaseName")}
          </DropdownMenuItem>
          {/* TENAX: new organization from the phone (task 1.12) */}
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => {
              setCompanyCreateOpen(true);
            }}
          >
            {translate("resources.companies.forcedCaseName")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => {
              setNoteCreateOpen(true);
            }}
          >
            {translate("resources.notes.forcedCaseName")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => {
              setTaskCreateOpen(true);
            }}
          >
            {translate("resources.tasks.forcedCaseName")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};

// TENAX: "More" opens the panel with the sections that do not fit in the
// bar (task 1.12, D-044); it replaces the settings button
const MoreButton = ({ isActive }: { isActive: boolean }) => {
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  return (
    <>
      <MoreSheet open={open} onOpenChange={setOpen} />
      <Button
        type="button"
        variant="ghost"
        aria-haspopup="dialog"
        className={cn(
          "flex-col gap-1 h-auto min-h-12 min-w-12 flex-1 py-1.5 px-0.5 rounded-md",
          isActive ? null : "text-muted-foreground",
        )}
        onClick={() => setOpen(true)}
      >
        <Ellipsis className="size-6" />
        <span className="text-[0.7rem] font-medium leading-none">
          {translate("tenax.mobile.more")}
        </span>
      </Button>
    </>
  );
};
